import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Lock } from "lucide-react";
import api, { assetUrl } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { ShareButtons } from "@/components/ShareButtons";

export default function LearnEntry() {
  const { slug } = useParams();
  const [entry, setEntry] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setEntry(null);
    api.get(`/learn/${slug}`).then((r) => setEntry(r.data)).catch(() => setError(true));
    window.scrollTo(0, 0);
  }, [slug]);

  if (error) return <div className="py-32 text-center text-muted-foreground">Entry not found.</div>;
  if (!entry) return <div className="py-32 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-14" data-testid="learn-entry-page">
      <SEO title={entry.title} description={entry.summary} />
      <Link to="/learn" className="text-sm text-muted-foreground flex items-center gap-1.5 mb-10 hover:text-foreground">
        <ArrowLeft size={14} /> Learn Library
      </Link>
      <Badge variant="secondary" className="font-normal mb-4">{entry.topic}</Badge>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-foreground mb-4" data-testid="learn-entry-title">{entry.title}</h1>
      <p className="text-muted-foreground italic mb-10">{entry.summary}</p>
      {entry.image && <img src={assetUrl(entry.image)} alt={entry.title} className="rounded-lg mb-10 w-full" />}
      {entry.audio_url && <audio controls src={assetUrl(entry.audio_url)} className="w-full mb-10" data-testid="learn-entry-audio" />}
      <div className="prose-orec" data-testid="learn-entry-content">
        {entry.content.split("\n\n").map((para, i) => <p key={i}>{para}</p>)}
      </div>
      {entry.locked && (
        <div className="silk-card rounded-lg p-8 text-center mt-8 border border-primary/40" data-testid="learn-locked-card">
          <Lock size={22} className="text-primary mx-auto mb-3" />
          <p className="font-display text-xl text-foreground mb-2">There's more to this one…</p>
          <p className="text-sm text-muted-foreground mb-6">The full article — and every article in the Learn Library — is open to Community Members. Free during our founding launch year.</p>
          <Button asChild className="rounded-full px-8" data-testid="learn-locked-cta">
            <Link to="/membership">Become a Community Member</Link>
          </Button>
        </div>
      )}
      <p className="text-xs text-muted-foreground mt-10">By {entry.author_name} · For educational purposes only — not medical advice.</p>

      <div className="mt-8">
        <ShareButtons title={`${entry.title} — O.R.E.C Learn Library`} />
      </div>

      {entry.related_practitioners?.length > 0 && (
        <div className="mt-14 border-t border-border pt-10" data-testid="learn-entry-related">
          <h2 className="font-display text-lg md:text-2xl text-foreground mb-6">
            Book a {entry.related_subcategory} session in Calgary
          </h2>
          <div className="space-y-3">
            {entry.related_practitioners.map((p) => (
              <Link key={p.id} to={`/practitioner/${p.id}`} className="silk-card rounded-lg p-4 flex items-center gap-4 block">
                {p.photo && <img src={p.photo} alt={p.name} className="w-14 h-14 rounded-full object-cover" />}
                <div className="flex-1">
                  <p className="font-display text-lg text-foreground">{p.business_name}</p>
                  <p className="text-xs text-muted-foreground">{p.name} · {p.city_name}</p>
                </div>
                <ArrowRight size={16} className="text-muted-foreground" />
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
